import { doc, getDoc } from 'firebase/firestore'
import { z } from 'zod'
import { coreReferenceSchema } from '../types/core'
import { db } from '../utils/firebase'

export const DAILY_SYNTHESIS_PROCESSOR_VERSION = '1.1.0'

const dailySynthesisItemSchema = z.object({
  kind: z.enum(['connection', 'unfinished_thread', 'next_move']),
  title: z.string().min(1).max(100),
  summary: z.string().min(1).max(280),
  evidenceRefs: z.array(coreReferenceSchema).min(1).max(5),
  confidence: z.number().min(0).max(1),
})

export const dailySynthesisArtifactSchema = z.object({
  schemaVersion: z.literal(1),
  processorVersion: z.literal(DAILY_SYNTHESIS_PROCESSOR_VERSION),
  fingerprint: z.string().min(1),
  dayKey: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  timezone: z.string().min(1),
  generatedAt: z.string().datetime({ offset: true }),
  synthesis: z.string().min(1).max(500),
  items: z.array(dailySynthesisItemSchema).min(1).max(5),
  uncertainties: z.array(z.string().min(1).max(280)).max(2),
})

export type DailySynthesisArtifact = z.infer<typeof dailySynthesisArtifactSchema>

export function parseDailySynthesisState(value: unknown): DailySynthesisArtifact | null {
  if (!value || typeof value !== 'object') return null
  const state = value as Record<string, unknown>
  if (state.lastOutcome !== 'generated' || !state.latest || typeof state.latest !== 'object') return null
  const parsed = dailySynthesisArtifactSchema.safeParse(state.latest)
  if (!parsed.success || parsed.data.fingerprint !== state.lastFingerprint) return null
  return parsed.data
}

export const coreDailySynthesisService = {
  async loadLatest(userId: string): Promise<DailySynthesisArtifact | null> {
    const snapshot = await getDoc(doc(db, 'users', userId))
    if (!snapshot.exists()) return null
    return parseDailySynthesisState(snapshot.data().dailySynthesisState)
  },
}
